var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable max-classes-per-file */
import { setUser as sentrySetUser, setTags as sentrySetTags, setExtras as sentrySetExtras, addBreadcrumb as sentryAddBreadcrumb, captureException as sentryCaptureException, } from '@sentry/core';
import { Severity } from '@sentry/types';
import { removeURLParams } from '@shm-open/utilities';
import { isUndefined, omitBy, pickBy } from 'lodash-es';
import { wait, withReady } from 'wait-ready';
import { addListener } from '../event';
const { afterReady, setReady } = wait();
addListener('sentryInited', setReady);
const [setUser, setTags, setExtras, addBreadcrumb, captureException] = [
    sentrySetUser,
    sentrySetTags,
    sentrySetExtras,
    sentryAddBreadcrumb,
    sentryCaptureException,
].map((func) => withReady(func, afterReady()));
const toSentryLevel = {
    debug: Severity.Debug,
    info: Severity.Info,
    warn: Severity.Warning,
    error: Severity.Error,
};
export class SentryLogTransport {
    updateContext(context) {
        const { tk, mid, app, app_version, binary_version, platform, path } = context, rest = __rest(context, ["tk", "mid", "app", "app_version", "binary_version", "platform", "path"]);
        if (tk) {
            // use tk as user id - to distinct devices when user not logged in
            setUser({ id: tk });
        }
        // add shm. prefix to avoid conflict with built-in tags
        const tags = omitBy({
            'shm.app': app,
            'shm.user_id': mid,
            'shm.app_version': app_version,
            'shm.binary_version': binary_version,
            'shm.platform': platform,
            'shm.path': removeURLParams(path),
        }, isUndefined);
        if (Object.keys(tags).length > 0) {
            // turn empty string to undefined to unset that tag
            // https://github.com/getsentry/sentry-javascript/pull/3108
            const finalTags = Object.entries(tags).reduce((acc, [key, value]) => {
                acc[key] = value === '' ? undefined : value;
                return acc;
            }, {});
            setTags(finalTags);
        }
        if (Object.keys(rest).length > 0) {
            setExtras(rest);
        }
    }
    exception(exception) {
        captureException(exception, {
            level: Severity.Fatal,
        });
    }
    write(log) {
        const { time, level, msg } = log, rest = __rest(log, ["time", "level", "msg"]);
        addBreadcrumb({
            level: toSentryLevel[level],
            message: msg,
            timestamp: time / 1000,
            data: rest,
        });
    }
}
export class SentryEventLogTransport {
    write(log) {
        const { time, level, msg, category } = log, rest = __rest(log, ["time", "level", "msg", "category"]);
        // use an exception wraps the msg
        const exception = new Error(msg);
        exception.name = `${level} log`;
        captureException(exception, {
            level: toSentryLevel[level],
            tags: pickBy({
                'shm.log_category': category,
            }),
            extra: rest,
        });
    }
}
